import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { produce } from 'immer';

import {
  SortableList,
  SortableListItem,
  SortableListItemDragHandle,
} from 'components/SortableList/SortableList';
import { Select, Switch, Input, sprinkles, Button } from 'components/ds';

import { updateVisualizeOperation } from 'actions/dataPanelConfigActions';
import { DatasetSchema } from 'types/datasets';
import {
  ColumnWidths,
  OPERATION_TYPES,
  COLUMN_FITS,
  VisualizeTableInstructions,
  ColumnFitOptions,
} from 'constants/types';
import { sortSchemaByOrderedColumnNames } from 'utils/general';
import { ReduxState } from 'reducers/rootReducer';

import * as styles from './index.css';

type Props = {
  instructions: VisualizeTableInstructions;
  schema: DatasetSchema;
};

export const TableLayoutConfig: FC<Props> = ({ instructions, schema }) => {
  const dispatch = useDispatch();

  const enableNewGrid = useSelector(
    (state: ReduxState) => !!state.currentUser?.team?.feature_flags.enable_new_grid,
  );

  const orderedColumnNames = instructions.orderedColumnNames;
  const orderedSchema = sortSchemaByOrderedColumnNames(schema, orderedColumnNames);
  const columnFit = instructions.columnFit ?? COLUMN_FITS.FILL;
  const columnWidths = instructions.columnWidths as ColumnWidths | undefined;

  const hasAWidthSet = Object.values(columnWidths ?? {}).some((width) => width > 0);

  const updateInstructions = (instructions: VisualizeTableInstructions) => {
    dispatch(updateVisualizeOperation(instructions, OPERATION_TYPES.VISUALIZE_TABLE));
  };

  return (
    <>
      <div className={sprinkles({ marginY: 'sp1.5' })}>
        <div className={sprinkles({ paddingX: 'sp1.5', marginBottom: 'sp1.5' })}>
          {enableNewGrid ? (
            <Select
              infoText="Automatically sizes columns. Any columns with a set pixel width won&rsquo;t autosize."
              label="Column Fit"
              onChange={(value) =>
                updateInstructions({ ...instructions, columnFit: value as COLUMN_FITS })
              }
              selectedValue={columnFit}
              values={ColumnFitOptions}
            />
          ) : null}
        </div>

        {enableNewGrid ? (
          <Switch
            className={sprinkles({ paddingX: 'sp1.5', marginBottom: 'sp1' })}
            label={{
              text: 'Wrap text',
              infoText:
                'We recommend being able to provide a uniform row height if this feature is used. Some features may not work as intended while this is turned on. This will apply to both the header and cells.',
            }}
            onChange={() => {
              const newInstructions = produce(instructions, (draft) => {
                draft.shouldTruncateText = !draft.shouldTruncateText;
              });

              updateInstructions(newInstructions);
            }}
            switchOn={!instructions.shouldTruncateText}
          />
        ) : null}

        <div className={sprinkles({ paddingX: 'sp1.5', marginBottom: 'sp1' })}>
          <Button
            fillWidth
            disabled={!hasAWidthSet}
            onClick={() => updateInstructions({ ...instructions, columnWidths: {} })}
            variant="secondary">
            Clear Column Widths
          </Button>
        </div>

        <SortableList
          getIdFromElem={(col) => col.name}
          onListUpdated={(newList) =>
            updateInstructions({
              ...instructions,
              orderedColumnNames: newList.map((col) => col.name),
            })
          }
          sortableItems={orderedSchema}>
          {orderedSchema.map((col) => (
            <SortableListItem key={col.name} sortId={col.name}>
              <div className={styles.item}>
                <SortableListItemDragHandle className={styles.dragIcon} />
                <div
                  className={sprinkles({
                    flex: 2,
                    paddingRight: 'sp.5',
                    truncateText: 'ellipsis',
                  })}>
                  <span className={styles.itemText}>{col.friendly_name || col.name}</span>
                </div>
                <Input
                  showInputButton
                  className={sprinkles({ flex: 1 })}
                  defaultValue={columnWidths?.[col.name] ? String(columnWidths[col.name]) : ''}
                  onSubmit={(newValue) => {
                    const newWidth = parseInt(newValue);

                    const newInstructions = produce(instructions, (draft) => {
                      if (!draft.columnWidths) draft.columnWidths = {};
                      const colWidths = draft.columnWidths as ColumnWidths;
                      if (newWidth > 0) {
                        colWidths[col.name] = newWidth;
                      } else if (col.name in draft.columnWidths) {
                        delete colWidths[col.name];
                      }
                    });
                    updateInstructions(newInstructions);
                  }}
                  placeholder="Width"
                />
              </div>
            </SortableListItem>
          ))}
        </SortableList>
      </div>
    </>
  );
};
